import React, { useEffect, useState } from "react";
import { Table, Button, Card, Container, Row, Col, Pagination, Modal, Form } from "react-bootstrap";
import { FormsService } from "../../../../service/forms.service";
import { FormPayload } from "../../../../service/types/types";
import { EyeFill, PencilFill, Link, PersonPlusFill } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import {UserService} from "../../../../service/users.service";
import {getHighestRole} from "../../../../service/utils/accessUtils";

export default function FormsTable() {
  const [forms, setForms] = useState<FormPayload[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [users, setUsers] = useState<{ rut: string; name: string }[]>([]);
  const [selectedRuts, setSelectedRuts] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchForms = async () => {
      let data;
      if(getHighestRole() === "WORKER")
        data = await FormsService.findAllByUser(localStorage.getItem("rut") || "");
      else
        data = await FormsService.findAllByOrg("m4");
      setForms(data);
    };
    fetchForms();
  }, []);

  // Abre el modal y carga usuarios
  const handleOpenAssignModal = async (form: FormPayload) => {
    setSelectedFormId(form.id);
    const selectedRuts: string[] = [];
    form.users?.map(u => {
      if (u.rut) selectedRuts.push(u.rut);
    });
    setSelectedRuts(selectedRuts);
    // 👇 Reemplaza esto con tu servicio real de usuarios
    const data = await UserService.find();
    setUsers(data);
    setShowModal(true);
  };

  const handleToggleRut = (rut: string) => {
    setSelectedRuts(prev =>
        prev.includes(rut) ? prev.filter(r => r !== rut) : [...prev, rut]
    );
  };

  const handleSave = async () => {
    if (!selectedFormId || selectedRuts.length === 0) return;
    setSaving(true);
    try {
      await FormsService.assignUsers(selectedFormId, selectedRuts);
      setShowModal(false);
    } catch (e) {
      alert("Error al asignar usuarios");
    } finally {
      setSaving(false);
    }
  };

  const handleView = (id: string) => navigate(`/m4/form/${id}`);
  const handleViewResults = (id: string) => navigate(`/m4/form/${id}/results`);
  const handleEdit = (id: string) => navigate(`/m4/form/${id}/edit`);
  const handleCopyUrl = (id: string) => {
    const fullUrl = `${window.location.origin}/m4/form/${id}`;
    navigator.clipboard.writeText(fullUrl);
    alert("URL copiada: " + fullUrl);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentForms = forms.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(forms.length / itemsPerPage);

  return (
      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col md={12}>
            <Card className="shadow-sm">
              <Card.Header className="bg-primary text-white">
                <h4 className="mb-0">📋 Lista de Formularios</h4>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive className="align-middle">
                  <thead className="table-light">
                  <tr>
                    <th>Nombre</th>
                    <th>Requiere Firma</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                  </thead>
                  <tbody>
                  {currentForms.map((form) => (
                      <tr key={form.id}>
                        <td>{form.name}</td>
                        <td>
                          {form.isSignRequired ? (
                              <span className="badge bg-success">Sí</span>
                          ) : (
                              <span className="badge bg-secondary">No</span>
                          )}
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Button variant="success" size="sm" onClick={() => handleView(form.id)}>
                              <PencilFill className="me-1" /> Completar
                            </Button>
                            <Button variant="info" size="sm" onClick={() => handleViewResults(form.id)}>
                              <EyeFill className="me-1" /> Ver resultados
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => handleCopyUrl(form.id)}>
                              <Link className="me-1" /> Copiar URL
                            </Button>
                            {getHighestRole() === "ADMIN" && (
                                <Button variant="outline-primary" size="sm" onClick={() => handleOpenAssignModal(form)}>
                                  <PersonPlusFill className="me-1" /> Asignar usuarios
                                </Button>
                            )}
                            {getHighestRole() === "ADMIN" && (
                                <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(form.id)}>
                                  <PencilFill className="me-1" /> Editar
                                </Button>
                            )}

                          </div>
                        </td>
                      </tr>
                  ))}
                  {forms.length === 0 && (
                      <tr>
                        <td colSpan={3} className="text-center text-muted">
                          No hay formularios disponibles
                        </td>
                      </tr>
                  )}
                  </tbody>
                </Table>

                {totalPages > 1 && (
                    <Pagination className="justify-content-center">
                      <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                      <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                      {[...Array(totalPages)].map((_, idx) => (
                          <Pagination.Item key={idx + 1} active={idx + 1 === currentPage} onClick={() => setCurrentPage(idx + 1)}>
                            {idx + 1}
                          </Pagination.Item>
                      ))}
                      <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                      <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 👇 Modal asignar usuarios */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Asignar usuarios al formulario</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
            {users.length === 0 ? (
                <p className="text-muted text-center">No hay usuarios disponibles</p>
            ) : (
                users.map(user => (
                    <Form.Check
                        key={user.rut}
                        type="checkbox"
                        id={`user-${user.rut}`}
                        label={`${user.name} (${user.rut})`}
                        checked={selectedRuts.includes(user.rut)}
                        onChange={() => handleToggleRut(user.rut)}
                        className="mb-2"
                    />
                ))
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || selectedRuts.length === 0}
            >
              {saving ? "Guardando..." : `Asignar (${selectedRuts.length})`}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
  );
}